import './LoginCard.css'

import type { FC } from 'react'
import { useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { attemptRefreshingSession } from 'supertokens-auth-react/recipe/session'

import AquaLogoBlue from '@/assets/icons/aqua-dental-logo-blue.svg?react'
import BankIdLogo from '@/assets/icons/bankId-logo-blue.svg?react'
import TandlakareLogo from '@/assets/icons/tandlakare-logo.svg?react'
import { Button } from '@/components/Button/Button'
import { PhoneLink } from '@/components/PhoneLink/PhoneLink'
import { RouterPath } from '@/models/enums'
import { activityTrackingService } from '@/services/ActivityTrackingService'
import LocalStorageService from '@/services/LocalStorageService'

interface LoginCardProps {
  phoneNumber: string | null
}

const LoginCard: FC<LoginCardProps> = ({ phoneNumber }) => {
  const { t } = useTranslation('login-page')
  const [showBankId, setShowBankId] = useState(false)
  const ret = encodeURIComponent(`${import.meta.env.VITE_WEBSITE_DOMAIN}/${RouterPath.Hub}/${RouterPath.Home}?r=true`)
  const APP_AUTH_IFRAME_URL = `${import.meta.env.VITE_WEBSITE_DOMAIN}/auth/link/general?r=${ret}`
  const navigate = useNavigate()

  const handleClickButton = () => {
    setShowBankId(true)
  }

  const handlePhoneClick = (phone: string) => {
    activityTrackingService.trackPhoneClick(phone)
  }

  useEffect(() => {
    const handleEventMessage = (event: MessageEvent<any>) => {
      if (event.data.action === 'finished' || event.data.action === 'authorized') {
        LocalStorageService.clearData('frontendCookies')
        attemptRefreshingSession()
        activityTrackingService.trackUserLogin().then(() => {
          navigate(`/${RouterPath.Hub}/${RouterPath.Home}`)
        })
      }

      if (
        event.data.action === 'started' &&
        event.data.data_bankid.redirect_needed &&
        event.data?.data_bankid?.app_url
      ) {
        window.location.href = event.data.data_bankid.app_url
      }
    }

    window?.addEventListener(
      'message',
      (event) => {
        handleEventMessage(event)
      },
      false
    )

    return () => {
      window?.removeEventListener('message', handleEventMessage, false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="login">
      <AquaLogoBlue width={250} />
      <p className="text-center text-sm leading-none">{t('login_description')}</p>
      {showBankId ? (
        <iframe title="bankid-iframe" src={APP_AUTH_IFRAME_URL} id="bankid-iframe" width={330} height={330} />
      ) : (
        <>
          <div className="login__bankid">
            <BankIdLogo />
          </div>
          <Button className="text-sm uppercase" size="button" onClick={handleClickButton}>
            {t('login_with_bankid')}
          </Button>
        </>
      )}
      <p className="leading-1 whitespace-pre-line text-center text-sm">
        {t('bankid_description')}
        <br />
        {phoneNumber && (
          <Trans
            i18nKey="login-page:call_us"
            components={[<PhoneLink key="phone" phone={phoneNumber} onClick={handlePhoneClick} />]}
          />
        )}
      </p>
      <div className="login__footer">
        <p className="text-center text-sm leading-none">{t('cooperation_description')}</p>
        <a href="https://tandlakare.se" target="_blank" rel="noopener noreferrer" aria-label="Visit Tandlakare website">
          <TandlakareLogo width={168} />
        </a>
      </div>
    </div>
  )
}

export default LoginCard
